import React, { useState, useImperativeHandle } from 'react';
import { SparklesIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { auth } from '../firebase';

const genId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const COUNT_OPTIONS = [5, 10, 15];

const COPY = {
  quizrush: {
    topicLabel: 'What\'s your quiz about?',
    topicPlaceholder: 'e.g. UK football history, 90s music trivia, my brand\'s product range',
    countLabel: 'How many questions?',
    hint: 'Players see multiple-choice questions one at a time. Aim for a clear theme — broader topics produce more variety, narrower topics test real expertise.',
    successLabel: 'questions ready',
    endpoint: 'generate-quiz-questions',
  },
  completelist: {
    topicLabel: 'What\'s your list about?',
    topicPlaceholder: 'e.g. Premier League clubs, EU capital cities, every Bond film',
    countLabel: 'How many items?',
    hint: 'Players race to name as many items as they can. Pick a topic with a clear finite list (countries, films, songs, products) — vague topics are hard to grade.',
    successLabel: 'items ready',
    endpoint: 'generate-list-items',
  },
};

/**
 * WizardContent — captures a topic from the user and auto-generates quiz
 * questions or list items via the existing AI endpoints. Used as a step in
 * the wizard for quizrush + completelist games.
 *
 * The component exposes a controller (via controllerRef) so the wizard
 * footer can drive Generate / Skip. State changes (canGenerate, isLoading,
 * inReview) are pushed up via onStateChange so the footer can toggle
 * button labels.
 */
export default function WizardContent({
  gameType,
  gameData,
  handleOptionsUpdate,
  onComplete,
  controllerRef,
  onStateChange,
}) {
  const copy = COPY[gameType] || COPY.quizrush;
  const options = gameData?.options || {};

  const [topic, setTopic] = useState('');
  const [count, setCount] = useState(gameType === 'completelist' ? 10 : 5);
  const [phase, setPhase] = useState('form'); // 'form' | 'loading' | 'review'
  const [error, setError] = useState(null);
  const [generatedCount, setGeneratedCount] = useState(0);

  const canGenerate = topic.trim().length > 0 && phase !== 'loading';

  // Keep wizard footer state in sync.
  React.useEffect(() => {
    onStateChange?.({
      canGenerate,
      isLoading: phase === 'loading',
      inReview: phase === 'review',
    });
  }, [canGenerate, phase, onStateChange]);

  const handleGenerate = async () => {
    if (phase === 'loading' || !topic.trim()) return;
    setError(null);
    setPhase('loading');

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60_000);

    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/app/${copy.endpoint}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ topic: topic.trim(), count }),
          signal: controller.signal,
        }
      );
      clearTimeout(timeout);

      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || `Generation failed (${res.status})`);
      }

      const data = await res.json();

      if (gameType === 'quizrush') {
        const generated = Array.isArray(data?.questions) ? data.questions : [];
        if (generated.length === 0) {
          setError('No questions could be generated. Try a different topic.');
          setPhase('form');
          return;
        }
        const newQs = generated.map((q) => ({
          id: genId('q'),
          questionText: q.questionText || '',
          image: null,
          optionA: q.optionA || '',
          optionB: q.optionB || '',
          optionC: q.optionC || '',
          optionD: q.optionD || '',
          correctAnswer: ['A', 'B', 'C', 'D'].includes(q.correctAnswer) ? q.correctAnswer : 'A',
        }));
        handleOptionsUpdate({ ...options, questions: newQs });
        setGeneratedCount(newQs.length);
      } else if (gameType === 'completelist') {
        const generated = Array.isArray(data?.items) ? data.items : [];
        if (generated.length === 0) {
          setError('No items could be generated. Try a different topic.');
          setPhase('form');
          return;
        }
        const newItems = generated.map((item) => ({
          id: genId('item'),
          answer: item.answer || '',
          aliases: Array.isArray(item.aliases)
            ? item.aliases.filter((a) => typeof a === 'string' && a.trim())
            : [],
          ...(item.detail ? { detail: item.detail } : {}),
        }));
        handleOptionsUpdate({
          ...options,
          listTitle: data?.listTitle || options.listTitle || topic.trim(),
          listItems: newItems,
        });
        setGeneratedCount(newItems.length);
      }

      setPhase('review');
    } catch (err) {
      clearTimeout(timeout);
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(err.message || 'Failed to generate. Please try again.');
      }
      setPhase('form');
    }
  };

  // Wizard footer drives these.
  useImperativeHandle(controllerRef, () => ({
    generate: handleGenerate,
    regenerate: () => {
      setPhase('form');
      setError(null);
    },
    confirm: () => onComplete?.(),
    skip: () => onComplete?.(),
  }), [handleGenerate, onComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  if (phase === 'loading') {
    return (
      <div className="gch-card p-6 sm:p-8 flex flex-col items-center justify-center text-center min-h-[260px]">
        <div className="flex items-center gap-3 mb-3">
          <SparklesIcon className="w-5 h-5 text-[var(--admin-accent)] animate-pulse" />
          <p className="text-base font-semibold gch-text-primary">
            Generating {count} {gameType === 'quizrush' ? 'questions' : 'items'}…
          </p>
        </div>
        <p className="text-sm gch-text-secondary max-w-md">
          This usually takes 10–20 seconds. You can tweak everything in the editor afterwards.
        </p>
      </div>
    );
  }

  if (phase === 'review') {
    return (
      <div className="gch-card p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-3">
          <CheckCircleIcon className="w-6 h-6 text-green-400" />
          <h3 className="text-lg font-semibold gch-text-primary">
            {generatedCount} {copy.successLabel}
          </h3>
        </div>
        <p className="text-sm gch-text-secondary mb-4">
          Generated from topic: <span className="gch-text-primary">{topic}</span>
        </p>
        <p className="text-xs gch-text-muted">
          Hit <span className="gch-text-primary font-medium">Next</span> to preview, or <span className="gch-text-primary font-medium">Regenerate</span> to try a different angle. You can edit every item in the campaign editor.
        </p>
      </div>
    );
  }

  // Form
  return (
    <div className="space-y-4">
      <div className="gch-card p-5 sm:p-6 space-y-4">
        <div>
          <label htmlFor="wizard-content-topic" className="block text-sm font-medium gch-text-primary mb-1.5">
            {copy.topicLabel}
          </label>
          <textarea
            id="wizard-content-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={copy.topicPlaceholder}
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2 rounded-lg gch-input-ghost border border-[var(--admin-border)] gch-text-primary placeholder:gch-text-muted text-sm focus:ring-2 focus:ring-[var(--admin-accent)] focus:outline-none resize-none"
          />
          <p className="text-xs gch-text-muted mt-1.5">{copy.hint}</p>
        </div>

        <div>
          <label className="block text-sm font-medium gch-text-primary mb-1.5">
            {copy.countLabel}
          </label>
          <div className="flex gap-2">
            {COUNT_OPTIONS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setCount(n)}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  count === n
                    ? 'bg-[var(--admin-accent)] border-[var(--admin-accent)] text-white'
                    : 'border-[var(--admin-border)] gch-text-secondary hover:gch-text-primary hover:border-white/30'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
